<template>
  <div>
    <el-dialog
      width="1125px"
      :visible="historyModalVisible"
      :before-close="handleCloseModal"
      :close-on-click-modal="false"
      @opened="buildFlowChart"
    >
      <div class="call-flow-info mb-10" v-if="currentCall">
        <h2 class="form-section-header">Call Flow View</h2>
        <div class="flex-three">
          <div class="call-flow-info-item flex">
            <h4 class="black-text mt-0 mb-0">Unique Id:</h4>
            <span class="ml-10 black-text">{{ currentCall.uniqueId }}</span>
          </div>
          <div class="call-flow-info-item flex">
            <h4 class="black-text mb-0 mt-0">Start Time:</h4>
            <span class="black-text ml-10">{{
              callFlowTimeFormat(currentCall.start)
            }}</span>
          </div>
          <div class="call-flow-info-item flex">
            <h4 class="black-text mb-0 mt-0">Duration:</h4>
            <span class="black-text ml-10">{{
              convertTime(currentCall.duration)
            }}</span>
          </div>
        </div>
        <div class="flex-three mt-5">
          <div class="call-flow-info-item flex">
            <h4 class="black-text mt-0 mb-0">Caller Number:</h4>
            <span class="ml-10 black-text">{{
              numberFormatter(currentCall.callerIdNumber)
            }}</span>
          </div>
          <div class="call-flow-info-item flex">
            <h4 class="black-text mb-0 mt-0">Number Dialed:</h4>
            <span class="black-text ml-10">{{
              numberFormatter(currentCall.dialedNum)
            }}</span>
          </div>
          <div class="call-flow-info-item flex">
            <h4 class="black-text mb-0 mt-0">Disposition:</h4>
            <span class="black-text ml-10">{{ currentCall.disposition }}</span>
          </div>
        </div>
      </div>

      <div>
        <div class="flex items-center justify-center pt-20">
          <div style="height:300px" id="tcr-numbers-table" class="vxe-table-height table-width card-base card-shadow--medium">
            <vxe-grid
              round
              border="inner"
              ref="xGrid"
              height="300px"
              auto-resize
              empty-text="No Data"
              highlight-hover-row
              show-overflow="tooltip"
              @cell-mouseenter="rowHover"
              @cell-mouseleave="rowLeave"
              :data="callData"
              :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
              :sort-config="{
              defaultSort: { field: 'provisionedDate', order: 'desc' },
            }"
              :columns="eventColumns"
            />
          </div>
        </div>
        </div>
    </el-dialog>

    <el-dialog
      width="440px"
      :visible="modalVisible"
      :before-close="handleCloseModal"
      :close-on-click-modal="false"
    >
      <h2 class="form-section-header">Filter Settings</h2>
      <h3 class="black-text mb-10">Select Date Range</h3>
      <el-date-picker
        :clearable="false"
        class="mr-10"
        style="margin-left: auto"
        v-model="historyDateRange"
        type="datetimerange"
        align="right"
        unlink-panels
        format="yyyy-MM-dd HH:mm"
        range-separator="To"
        start-placeholder="Start date"
        end-placeholder="End date"
        :picker-options="datePickerOptions"
      />

      <el-form id="records-advanced-modal" class="form-modal">
        <h3 class="black-text mb-10">Select Call Direction</h3>
        <el-radio-group v-model="direction">
          <el-radio-button type="default" label="Inbound"
            >Inbound</el-radio-button
          >
          <el-radio-button label="Outbound">Outbound</el-radio-button>
          <el-radio-button label="Internal">Internal</el-radio-button>
          <el-radio-button label="All">All</el-radio-button>
        </el-radio-group>

        <h3 class="black-text mb-0">Filter By</h3>
        <el-select
          v-model="primaryFilter"
          placeholder="Select Filter"
          clearable
          style="margin-top: 0"
          v-if="userIsReportsAdmin"
        >
          <template slot="prefix"> Filter By </template>
          <el-option value="branch" label="Branch" />
          <el-option value="phoneNumber" label="Phone number" />
          <el-option value="callerName" label="Caller name" />
          <el-option value="callId" label="Call Id" />
          <el-option
            value="user"
            :label="direction === 'Inbound' ? 'User (Answered Only)' : 'User'"
          />
        </el-select>

        <dads-select
          v-if="primaryFilter === 'user' && userIsReportsAdmin"
          v-model="extNumber"
          placeholder="Select A User"
          allow-create
          remote
          filterable
          clearable
          :remote-method="searchExtensions"
        >
          <el-option
            v-for="(item, index) in extOptions"
            :key="index"
            :label="item.name + '-' + item.number"
            :value="item.number"
          />
          <template slot="prefix"> Filter By User </template>
        </dads-select>

        <dads-select
          v-if="primaryFilter === 'user' && !userIsReportsAdmin"
          v-model="extNumber"
          placeholder="Select An Agent"
          allow-create
          remote
          filterable
          clearable
          :remote-method="searchQueueMembers"
        >
          <el-option
            v-for="(item, index) in extOptions"
            :key="index"
            :label="
              item.membername.replace(/_/g, ' ') +
              ' - ' +
              item.memberdevice.split('-')[0]
            "
            :value="item.memberdevice.split('-')[0]"
          />
          <template slot="prefix"> Filter By Queue Member </template>
        </dads-select>

        <dads-select
          v-if="primaryFilter === 'branch'"
          v-model="branch"
          placeholder="Select A Branch"
          filterable
        >
          <el-option
            v-for="(item, index) in branchOptions"
            :key="index"
            :label="item.name"
            :value="item.id"
          />
          <template slot="prefix"> Filter By Branch </template>
        </dads-select>

        <dads-select
          v-if="primaryFilter === 'phoneNumber'"
          v-model="phoneNumber"
          placeholder="Select A Number"
          filterable
          clearable
          remote
          allow-create
          :remote-method="searchNumbers"
        >
          <el-option
            v-for="(item, index) in numberOptions"
            :key="index"
            :label="numberFormatter(item.number)"
            :value="item.number"
          />
          <template slot="prefix"> Filter By Number </template>
        </dads-select>

        <el-input
          v-if="primaryFilter === 'callerName'"
          v-model="callerName"
          placeholder="Enter name"
        >
          <template slot="prefix"> Caller name </template>
        </el-input>

        <el-input
          v-if="primaryFilter === 'callId'"
          v-model="callId"
          placeholder="Enter Call Id"
        >
          <template slot="prefix"> Call Id </template>
        </el-input>

        <h3 class="black-text mb-5 mt-5">Select Dispositions</h3>
        <el-checkbox-group v-model="dispositions" class="dispositions-group">
          <div>
            <el-checkbox label="ANSWERED">Answered</el-checkbox>
          </div>
          <div>
            <el-checkbox label="NO ANSWER">No Answer</el-checkbox>
          </div>
          <div>
            <el-checkbox label="BUSY">Busy</el-checkbox>
          </div>
          <div>
            <el-checkbox label="FAILED">Failed</el-checkbox>
          </div>
        </el-checkbox-group>

        <div>
          <div class="flex align-center">
            <h3 class="black-text mb-10">Select Presented Filter</h3>
            <el-popover width="350px" placement="top" trigger="hover">
              <i class="more-info-icon el-icon-question" slot="reference" />
              <p class="text-left black-text" style="max-width: 300px">
                Presented means a call attempted to reach a user.
              </p>
            </el-popover>
          </div>

          <el-radio-group v-model="presentedFilter">
            <el-radio-button label="Presented">Presented</el-radio-button>
            <el-radio-button label="Not Presented"
              >Not Presented</el-radio-button
            >
            <el-radio-button label="All">All</el-radio-button>
          </el-radio-group>
        </div>

        <el-button @click="doAdvanced" class="mt-20"> Update </el-button>
      </el-form>
    </el-dialog>

    <div id="call-records-view" class="page-table scrollable only-y">
      <div class="no-table-box page-header">
        <div class="flex justify-space-between mb-10">
          <h2 class="mb-0 mt-0 text-left">
            <v-svg sprite="flow" class="voxo-blue"></v-svg>
            Call Records
          </h2>
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total: {{ total | number }}
          </el-tag>
        </div>

        <div class="flex align-center mb-10">
          <el-radio-group
            v-model="presetSelection"
            @change="doPreset"
            class="hidden-xs-only"
          >
            <el-radio-button type="default" label="yesterday"
              >Yesterday</el-radio-button
            >
            <el-radio-button type="default" label="last10"
              >Last 10 Days</el-radio-button
            >
            <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
          </el-radio-group>

          <el-tooltip placement="top" :content="selectedDateRange">
            <template slot="content">
              {{ selectedDateRange }}
              <br />
              Presented: {{ presentedFilter }}
            </template>
            <el-button @click="modalVisible = true" class="ml-10">
              Filter
            </el-button>
          </el-tooltip>
          <el-button
            @click="resetTable"
            v-if="advancedActive"
            style="background-color: #f25a6b; color: #fff"
          >
            Reset
          </el-button>
          <el-dropdown class="ml-10">
            <el-button>
              Export
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="doExport">
                <v-svg class="success-text" sprite="list" />
                CSV
              </el-dropdown-item>
              <el-dropdown-item @click.native="openScheduledReportModal">
                <v-svg class="voxo-orange" sprite="clock" />
                Schedule Report
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="150"
            :pager-count="5"
            :total="total"
            v-if="total > limit"
            style="margin-left: auto"
          />
        </div>

        <div class="vxe-table-height-tabs card-base card-shadow--medium">
          <vxe-grid
            round
            border="inner"
            ref="xGrid"
            height="100%"
            auto-resize
            empty-text="No Data"
            show-overflow="tooltip"
            highlight-hover-row
            @cell-click="cellClicked"
            @cell-mouseenter="rowHover"
            @cell-mouseleave="rowLeave"
            :data="tableData"
            :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
            :columns="tableColumns"
          />
        </div>
      </div>
    </div>

    <schedule-report-modal
      :modalVisible="scheduleReportVisible"
      :filterSettings="filterSettings"
      @cancelOperation="scheduleReportVisible = false"
    />
  </div>
</template>

<script>
import "jointjs/dist/joint.min.css";
import feathersClient, {apiV2Host} from "@/feathers/feathers-client";
import debounce from "lodash/debounce";
import moment from "moment";
import diagramFactory from "../diagram-factory";
import ScheduleReportModal from "@/components/schedule-reports/ScheduleReportModal.vue";

import * as joint from "jointjs";
import uniqBy from "lodash/uniqBy";
import DadsSelect from "@/components/select";
import {ExportToCsv} from "export-to-csv";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "CallRecordsTable",

  props: {
    branchOptions: { required: true },
  },

  components: { DadsSelect, ScheduleReportModal },

  data() {
    return {
      scheduleReportVisible: false,
      queryParams: null,
      filterSettings: null,

      presentedFilter: "All",
      visibleActionsId: null,
      flowGraph: null,
      loadingCallFlow: false,
      colors: ["red", "blue", "black", "orange", "green"],
      cellViewFrom: null,
      from: null,
      to: null,
      search: "",
      page: 1,
      limit: 150,
      total: 0,
      loadingData: false,
      advancedActive: false,
      modalVisible: false,
      historyModalVisible: false,
      presetSelection: "init",
      tableData: [],
      dispositions: ["ANSWERED", "NO ANSWER", "BUSY", "FAILED"],
      primaryFilter: "",
      phoneNumber: "",
      extNumber: "",
      branch: "",
      callerName: "",
      callId: "",
      numberOptions: [],
      extOptions: [],
      startDate: null,
      endDate: null,
      direction: "All",
      currentCall: null,
      callSteps: [],
      callData: [],
      limitWide: 4,
      boxHeight: 85,
      linkLength: 60,
      boxWidth: 180,
      boxMargin: 60,
      rowNum: 0,
      colNum: 1,
      diagramWidth: 1000,
      lastFlowId: null,
      buildNewChart: true,
      multiDialInfo: [],
      multiDialList: [],
      showMultiDialTable: false,
      extNameKey: {},
      exportCSV: false,
      historyDateRange: [moment(), moment()],
      eventColumns: [
        {
          field: "event",
          title: "Event",
          sortable: false,
          showOverflow: false,
          width: 500
        },
        {
          field: "time",
          title: "Time",
          sortable: false,
          minWidth: 250,
          showOverflow: false
        },
      ],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-blue" sprite="flow" />
                </div>,
              ];
            },
          },
        },
        {
          field: "start",
          title: "Time",
          minWidth: 150,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.start)}</span>];
            },
          },
        },
        {
          field: "callerIdNumber",
          title: "Caller Number",
          minWidth: 200,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [
                <span>
                  {row.direction === "OUT" && row.outgoingCidNum
                    ? `${this.numberFormatter(
                        row.callerIdNumber
                      )} | ${this.numberFormatter(row.outgoingCidNum)}`
                    : this.numberFormatter(row.callerIdNumber)}
                </span>,
              ];
            },
          },
        },
        {
          field: "callerIdName",
          title: "Caller Name",
          minWidth: 200,
          showOverflow: true,
        },
        {
          field: "dialedNum",
          title: "Dialed Number",
          minWidth: 150,
          slots: {
            default: ({ row }) => {
              return [<span>{this.numberFormatter(row.dialedNum)}</span>];
            },
          },
        },
        {
          field: "direction",
          title: "Direction",
          minWidth: 125,
        },
        {
          field: "disposition",
          title: "Disposition",
          minWidth: 150,
        },
        {
          field: "presented",
          title: "Presented",
          minWidth: 100,
          slots: {
            default: ({ row }) => {
              return [<span>{+row.presented ? "Yes" : "No"}</span>];
            },
          },
        },
        {
          field: "duration",
          title: "Duration",
          minWidth: 100,
          slots: {
            default: ({ row }) => {
              return [<span>{this.convertTime(row.duration)}</span>];
            },
          },
        },
        {
          field: "whoAnswered",
          title: "Who Answered",
          minWidth: 150,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.numberFormatter(row.whoAnswered)}</span>];
            },
          },
        },
        {
          field: "packetLoss",
          title: "Packet Loss",
          minWidth: 120,
          slots: {
            default: ({ row }) => {
              return [<span>{row.qos ? `${row.packetLoss} %` : "N/A"}</span>];
            },
          },
        },
        {
          field: "percentJitter",
          title: "Jitter",
          minWidth: 100,
          slots: {
            default: ({ row }) => {
              return [
                <span>{row.qos ? `${row.percentJitter} %` : "N/A"}</span>,
              ];
            },
          },
        },
        {
          field: "mos",
          title: "MOS",
          minWidth: 100,
        },
        {
          field: "actions",
          title: "",
          minWidth: "200",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"delete-" + row.id}
                    icon="mdi mdi-history"
                    onClick={() => this.getCallHistory(row)}
                    class="table-action-button icon-blue"
                  />
                </div>,
              ];
            },
          },
        },
      ],
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    colorTheme() {
      return this.$store.state.layout.theme;
    },
    user() {
      return this.$store.state.auth.user;
    },
    userOffsetMinutes() {

      return this.$store.state.userTimeOffset;
    },
    selectedDateRange() {
      if (this.endDate && this.startDate) {
        const dateStart = moment(this.startDate).format("M/D/YY HH:mm");
        const dateEnd = moment(this.endDate).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
    userIsReportsAdmin() {
      const roles = [1, 3, 9, 11, 12];
      const { userRole, enableReporting } = this.user;
      return roles.includes(+userRole) || enableReporting;
    },
  },

  watch: {
    page: debounce(function () {
      this.queryCallRecords();
    }, 500),
    selectedTenant() {
      this.resetTable();
    },
    primaryFilter(val) {
      if (val) {
        this.callId = "";
        this.callerName = "";
        this.phoneNumber = "";
        this.branch = "";
        this.extNumber = "";
      }
    },
    queryParams(params) {
      if (params) {
        const {
          queryType,
          tenantId,
          direction,
          primaryFilter,
          extNumber,
          branch,
          phoneNumber,
          callerName,
          callId,
          dispositions,
          presentedFilter,
          timezone,
        } = params;

        const queryParams = {
          queryType,
          tenantId: {
            label: this.selectedTenant.name,
            value: tenantId,
          },
          direction,
          primaryFilter: {
            label: "",
            value: "",
          },
          extNumber: { label: "", value: "" },
          branch: { label: "", value: "" },
          phoneNumber: {
            label: phoneNumber ? this.numberFormatter(phoneNumber) : "",
            value: phoneNumber ? phoneNumber : "",
          },
          callerName,
          callId,
          dispositions,
          presentedFilter,
          timezone,
        };

        if (primaryFilter) {
          const primaryFilterMap = {
            branch: "Branch",
            phoneNumber: "Phone number",
            callerName: "Caller Name",
            callId: "Call Id",
            user: "User",
          };

          queryParams["primaryFilter"] = {
            label: primaryFilterMap[primaryFilter],
            value: primaryFilter,
          };
        }

        if (extNumber && this.userIsReportsAdmin) {
          const { name, number } = this.extOptions.find(
            (e) => e.number === extNumber
          );
          queryParams["extNumber"] = {
            label: `${name}-${number}`,
            value: extNumber,
          };
        }

        if (extNumber && !this.userIsReportsAdmin) {
          const { membername, memberdevice } = this.extOptions.find(
            (e) => e.memberdevice.split("-")[0] === extNumber
          );
          queryParams["extNumber"] = {
            label:
              membername.replace(/_/g, " ") +
              " - " +
              memberdevice.split("-")[0],
            value: extNumber,
          };
        }

        if (branch) {
          const { name, id } = this.branchOptions.find((b) => branch === b.id);
          queryParams["branch"] = { label: name, value: id };
        }

        this.filterSettings = {
          servicePath: "call-history",
          query: queryParams,
        };
      }
    },
  },

  methods: {
    openScheduledReportModal() {
      this.scheduleReportVisible = true;
    },

    queryCallRecords() {
      this.loadingData = true;
      this.queryParams = {
        queryType: "callHistory",
        tenantId: this.selectedTenant.id,
        direction: this.direction,
        primaryFilter: this.primaryFilter,
        dispositions: this.dispositions,
        extNumber: this.extNumber,
        phoneNumber: this.phoneNumber.replace(
          /['":;/{}()[|\-.,<>?_=+ \]]/g,
          ""
        ),
        callerName: this.callerName,
        callId: this.callId,
        branch: this.branch,
        timezone: this.user.timezone,
        presentedFilter: this.presentedFilter,
      };

      feathersClient
        .service("call-history")
        .find({
          query: {
            startDate: this.startDate,
            endDate: this.endDate,
            $limit: this.limit,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
            exportCSV: this.exportCSV,
            $sort: { start: -1 },
            ...this.queryParams,
          },
        })
        .then((res) => {
          if (!this.exportCSV) {
            this.limit = res.limit;
            this.pages = Math.ceil(res.total / res.limit);
            this.total = res.total;
            this.page = this.page > this.pages ? 1 : this.page;
            this.tableData = res.data;
          }

          if (this.exportCSV) {
            if (!res.url) {
              this.$notify({
                title: "Not Found",
                message: "File Not Found.",
                type: "warning",
              });
            } else {
              this.downloadCSV(res);
            }
          }

          this.exportCSV = false;
          this.loadingData = false;
        })
        .catch(() => {
          this.loadingData = false;
        });
    },
    singlePageCSV() {
      this.downloadCSV(this.singlePageRes);
    },
    resetTable() {
      this.advancedActive = false;
      this.dispositions = ["ANSWERED", "NO ANSWER", "BUSY", "FAILED"];
      this.extOptions = [];
      this.numberOptions = [];
      this.advancedActive = false;
      this.direction = "All";
      this.presentedFilter = "All";

      // ⭐️ Clear Primary Filter ⭐️ //
      this.primaryFilter = "";
      this.extNumber = "";
      this.branch = "";
      this.phoneNumber = "";
      this.callerName = "";
      this.callId = "";

      this.doPreset("init");
    },

    async doPreset(val) {
      //if this is a queue managers we will have to manually build and filter extensions
      if (!this.extNumber.length && !this.userIsReportsAdmin) {
        this.primaryFilter = "user";
        await this.findQueueMembers();
      }

      if (val === "init") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryCallRecords();
      }

      if (val === "last10") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryCallRecords();
      }

      if (val === "lastMonth") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryCallRecords();
      }

      if (val === "yesterday") {
        this.endDate = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryCallRecords();
      }
    },
    doAdvanced() {
      this.startDate = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm"
      );
      this.endDate = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm"
      );
      this.queryCallRecords();
      this.handleCloseModal();
      this.advancedActive = true;
    },
    doExport() {
      this.$confirm(`Export data for ${this.total} records?`, {
        type: "warning",
      })
        .then(() => {
          if (this.pages === 1) {
            //export from browser
            let csvData = [];
            for (let i = 0; i < this.tableData.length; i++) {
              const csvObject = {};
              let exclusionList = [
                "id",
                "_XID",
                "callerIdName",
                "qos",
                "dialDirection",
              ];
              for (let key in this.tableData[i]) {
                if (exclusionList.includes(key) === false) {
                  csvObject[key] = this.tableData[i][key]
                    ? this.tableData[i][key]
                    : "";
                }
              }
              csvData.push(csvObject);
            }

            const options = {
              filename: "call_records",
              showLabels: true,
              headers: [
                "Start",
                "End",
                "Call Id",
                "Linked Call Id",
                "Linked Call Id",
                "Outbound Caller Id",
                "Caller Number",
                "Caller Name",
                "Dialed Number",
                "Direction",
                "Disposition",
                "Presented (1 = Yes) (0 = No)",
                "Duration (seconds)",
                "Who Answered",
                "Avg Package Loss (%)",
                "Avg Percent Jitter (%)",
                "Mean Opinion Score (MOS)",
              ],
            };

            let csvReorder = csvData.map((obj) => {
              let reorderObj = {};
              reorderObj["start"] = this.timeFormatterExport(obj.start);
              reorderObj["End"] = this.timeFormatterExport(obj.endTime);
              reorderObj["uniqueId"] = obj.uniqueId;
              reorderObj["prevUniqueId"] = obj.prevUniqueId;
              reorderObj["thirdUniqueId"] = obj.thirdUniqueId;
              reorderObj["outBoundCallerId"] = obj.outgoingCidNum;
              reorderObj["callerNumber"] = obj.callerIdNumber;
              reorderObj["callerName"] = obj.callerName;
              reorderObj["dialedNum"] = obj.dialedNum;
              reorderObj["direction"] = obj.direction;
              reorderObj["disposition"] = obj.disposition;
              reorderObj["presented"] = obj.presented;
              reorderObj["duration"] = obj.duration;
              reorderObj["whoAnswered"] = obj.whoAnswered;
              reorderObj["packetLoss"] = obj.packetLoss;
              reorderObj["percentJitter"] = obj.percentJitter;
              reorderObj["mos"] = obj.mos;
              return reorderObj;
            });

            csvData.push(csvReorder);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(csvReorder);
          } else {
            this.exportCSV = true;
            this.queryCallRecords();
          }
        })
        .catch(() => true);
    },
    doExportCallFlow() {
      this.$confirm(`Too many nodes in call flow. Export to CSV?`, {
        type: "warning",
      })
        .then(() => {
          {
            let isExport = true;
            let steps = [...this.callSteps];
            let csvData = [];

            for (let i = 0; i < this.callSteps.length; i++) {
              let csvObject = {};
              let exclusionList = ["calledNum"];
              for (let key in steps[i]) {
                if (!exclusionList.includes(key)) {
                  csvObject = diagramFactory(steps[i], isExport);
                }
              }
              csvData.push(csvObject);
            }

            csvData = csvData.filter(function (el) {
              return el.event !== "Dial Multiple";
            });

            const options = {
              filename: "call_flow",
              showLabels: true,
              headers: [
                "Action",
                "Start Time",
                "Caller Number",
                "Caller Name",
                "Description",
              ],
            };

            let csvReorder = csvData.map((obj) => {
              let reorderObj = {};
              reorderObj["event"] = obj.event;
              reorderObj["time"] = obj.time + "-05:00";
              reorderObj["callerNumber"] = obj.callerNumber;
              reorderObj["callerName"] = obj.callerName;
              reorderObj["description"] = obj.description;
              return reorderObj;
            });

            const cleanUndefined = (key, value) => (value ? value : "");

            const csvReorderClean = JSON.stringify(csvReorder, cleanUndefined);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(csvReorderClean);
            this.exportCSV = true;
            this.handleCloseModal();
          }
        })
        .catch(() => true);
    },
    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    },
    searchNumbers(query) {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");
      feathersClient
        .service("dids")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            number: { $like: `%${searchTerm}%` },
            $limit: 25,
          },
        })
        .then((res) => {
          this.numberOptions = res;
        });
    },
    searchExtensions(query) {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");
      feathersClient
        .service("extensions")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $or: [
              { number: { $like: `%${searchTerm}%` } },
              { name: { $like: `%${searchTerm}%` } },
            ],
            $limit: 25,
          },
        })
        .then((res) => {
          this.extOptions = res;
        });
    },
    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },
    timeFormatter(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("M/DD h:mm:ss A")
        : moment(value).add(offsetMinutes, "minutes").format("M/DD h:mm:ss A");
    },
    timeFormatterExport(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("MM-DD-YYYY hh:mm:ss A")
        : moment(value)
            .add(offsetMinutes, "minutes")
            .format("MM-DD-YYYY hh:mm:ss A");
    },
    callFlowTimeFormat(time) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(time)
            .subtract(offsetMinutes, "minutes")
            .format("M/DD h:mm:ss A")
        : moment(time).add(offsetMinutes, "minutes").format("M/DD h:mm:ss A");
    },
    handleCloseModal() {
      this.modalVisible = false;
      this.historyModalVisible = false;
      this.multiDialList = [];
      this.showMultiDialTable = false;
      this.callData = [];
    },
    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },
    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },
    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    pageSet(page) {
      this.page = page;
    },
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.getCallHistory(data.row);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },
    async getCallHistory(row) {
      if (row.uniqueId !== this.lastFlowId) {
        this.buildNewChart = true;
        this.loadingData = true;
        this.lastFlowId = row.uniqueId;
        this.callId = row.callId;
        await feathersClient
          .service("call-history")
          .get(null, {
            query: {
              tenantId: this.selectedTenant.id,
              ...row,
            },
          })
          .then((res) => {
            this.historyModalVisible = true;
            this.loadingData = false;
            this.currentCall = res;
            this.callSteps = res.callSteps;
            this.rowNum = 0;
          });
      } else {
        this.buildNewChart = false;
      }
      this.loadingCallFlow = true;
      this.historyModalVisible = true;
    },
    addNode(step, index) {
      const rect = new joint.shapes.standard.HeaderedRectangle();
      const increasedSpacing = 1.8;

      this.rowNum =
        (index - 1) % this.limitWide === 0 ? this.rowNum + 1 : this.rowNum;
      this.colNum =
        index % this.limitWide === 0 ? this.colNum + 1 : index % this.limitWide;
      let x = 20 + (this.colNum - 1) * (this.boxWidth + this.linkLength * increasedSpacing);
      let y = 20 + (this.rowNum - 1) * (this.boxHeight + this.boxMargin);

      if (this.rowNum % 2 === 0) {
        x = 1143 - x - this.boxWidth - 60;
      }

      const styleConfig = diagramFactory(step);
      const { subText, bgColorHeader, headerColor, mainText } = styleConfig;
      const bgColor = this.colorTheme === "theme-dark" ? "#191d24" : "#fff";
      const labelColor = this.colorTheme === "theme-dark" ? "#ececec" : "#2c2d30";
      const headerBG = bgColorHeader
        ? bgColorHeader
        : this.colorTheme === "theme-dark"
          ? "#ececec"
          : "#ced6e0";

      rect.position(x, y);
      rect.resize(this.boxWidth, this.boxHeight);
      rect.attr({
        header: {
          fill: headerBG,
          stroke: "none",
        },
        body: {
          fill: bgColor,
          rx: 8,
          stroke: "none",
          overflow: "hidden",
        },
        bodyText: {
          text: `${subText}`,
          fill: labelColor,
          "text-decoration": subText === "View All" ? "underline" : "none",
        },
        headerText: {
          text: mainText || step.event,
          fill: headerColor,
          "font-weight": "bold",
        },
      });
      rect.addTo(this.flowGraph);

      return rect;
    },
    addLink(source, target, firstNode, nextNode) {
      const isDarkTheme = this.colorTheme === "theme-dark";
      const color = isDarkTheme ? "#ececec" : "rgba(0,0,0,0.5)";
      const textColor = isDarkTheme ? "rgba(0,0,0,1)" : "#ececec";
      const rectColor = isDarkTheme ? "#ececec" : "rgba(0,0,0,1)";

      const diffInSeconds = this.calculateTimeDifference(firstNode, nextNode);

      const link = new joint.shapes.standard.Link();
      link.attr({
        line: {
          stroke: color,
        },
      });

      link.labels([
        {
          position: 0.5,
          attrs: {
            text: {
              text: `  ${diffInSeconds}  `,
              fill: textColor,
              fontSize: 12,
            },
            rect: {
              fill: rectColor,
            },
          },
        },
      ]);

      link.source(source);
      link.target(target);
      link.addTo(this.flowGraph);
    },
    async buildFlowChart() {
      const url = `${apiV2Host}/admin/reporting/calls/call-journey`;
      const body = {
        callId: this.callId,
      };
      const callRecords = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + this.$store.state.auth.accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const { callJourney }  = await callRecords.json();
      for (let i = 0; callJourney.length > i; i++) {
        this.callData.push({
          event: callJourney[i].data,
          time: moment(callJourney[i].timestamp).format("hh:mm:ss.SSS A")
        });
      }
    },
    convertTime(time) {
      const hours = Math.floor(time / 3600);
      const remainderSeconds = time % 3600;
      const minutes = Math.floor(remainderSeconds / 60);
      const seconds = Math.round(remainderSeconds % 60);

      if (hours > 0) {
        return hours + "hr " + minutes + "m";
      }
      if (minutes > 0) {
        return minutes + "m";
      }
      if (seconds > 0) {
        return seconds + "s";
      }
      return "0";
    },
    calculateTimeDifference(firstNode, nextNode) {
      if (!nextNode) return "0s";

      const { timeCST } = firstNode;
      const { timeCST: nextTimeCST } = nextNode;

      let firstTime = moment(timeCST, "YYYY-MM-DD HH:mm:ss.SSSSSS");
      let nextTime = moment(nextTimeCST, "YYYY-MM-DD HH:mm:ss.SSSSSS");

      let diff = nextTime.diff(firstTime, "milliseconds");

      return this.convertTimeWithMillisecondSupport(diff);
    },
    convertTimeWithMillisecondSupport(time) {
      const hours = Math.floor(time / 3600000);
      const remainingMillisecondsAfterHours = time % 3600000;
      const minutes = Math.floor(remainingMillisecondsAfterHours / 60000);
      const remainingMillisecondsAfterMinutes =
        remainingMillisecondsAfterHours % 60000;
      const seconds = Math.floor(remainingMillisecondsAfterMinutes / 1000);
      const milliseconds = Math.round(remainingMillisecondsAfterMinutes % 1000);

      if (hours > 0) {
        return hours + "h " + minutes + "m " + seconds + "s";
      }
      if (minutes > 0) {
        return minutes + "m " + seconds + "s";
      }
      if (seconds > 0) {
        return seconds + "s";
      }
      if (milliseconds > 0) {
        return milliseconds + "ms";
      }
      return "0s";
    },
    async findQueueMembers() {
      const { adminQueues } = this.user;

      const allowedMembers = await feathersClient
        .service("queuemembers-allowed")
        .find({
          query: {
            queueName: { $in: adminQueues },
            $limit: 25,
            $sort: {
              membername: 1,
            },
          },
        });

      const members = await feathersClient.service("queuemembers").find({
        query: {
          queueName: { $in: adminQueues },
          $limit: 25,
          $sort: {
            membername: 1,
          },
        },
      });

      const allMembers = [...allowedMembers, ...members];
      this.extOptions = uniqBy(allMembers, "membername");
      this.extNumber = this.extOptions[0].memberdevice.split("-")[0];
    },
    async searchQueueMembers(query) {
      const { adminQueues } = this.user;

      if (query.length) {
        const allowedMembers = await feathersClient
          .service("queuemembers-allowed")
          .find({
            query: {
              queueName: { $in: adminQueues },
              membername: { $like: `%${query}%` },
              $limit: 25,
              $sort: {
                membername: 1,
              },
            },
          });

        const members = await feathersClient.service("queuemembers").find({
          query: {
            queueName: { $in: adminQueues },
            membername: { $like: `%${query}%` },
            $limit: 25,
            $sort: {
              membername: 1,
            },
          },
        });

        const allMembers = [...allowedMembers, ...members];
        this.extOptions = uniqBy(allMembers, "membername");
      } else {
        this.extOptions = [];
      }
    },
  },
  mounted() {
    this.doPreset("init");
  },
  beforeDestroy() {},
  filters: {
    number(value) {
      return !isNaN(value)
        ? new Intl.NumberFormat("en-US").format(value)
        : value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/_variables.scss";

.el-form {
  .el-select {
    margin-left: 0;
    margin-top: 0;
    margin-bottom: 0;
  }
}

.dispositions-group {
  .el-checkbox {
    margin-left: 0;
    display: inline-block;
    margin-top: 5px;
  }
}

.joint-paper {
  .joint-type-standard-headeredrectangle {
    border-radius: 8px;
  }
}

.call-flow-wrapper {
  position: relative;
  margin: 0 auto;
  text-align: center;
  width: 50%;
}

.flex-three {
  display: flex;
  > * {
    flex-basis: 33.3333333%;
  }
}

.overlay-wrapper {
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
}

.more-info-icon {
  font-size: 18px;
  color: $voxo-blue;
  margin-top: 8px;
  margin-left: 5px;
  align-self: flex-end;
  margin-bottom: 5px;
}
</style>
